import { UnexpectedApiResponseError } from 'src/Libs/errors'
import { ApiResult } from 'src/Libs/api'
import { PowerMeterModel } from 'src/Libs/power-meters'
import { AuthFetchWithResponse } from 'src/Types/api'
import { StatusCodes } from 'src/Libs/http-status-codes'
import { PowerMeterUpdateDto, PowerMeterOverviewDto } from 'src/Libs/power-meters/dto'
import { fromDtoToModel } from 'src/Libs/power-meters/transformer'
import { DifferenceModel } from 'src/Elements/Dashboard/HistoryValuesModel'
import { handleFetchError } from 'src/Libs/api/utils'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { PowerMeterIds, PowerMeterListItem, PowermeterHardwareModel } from '../models/power-meter.model'
import { PowerMeterDto } from '../dto/power-meter.dto'
import fromHardwareDtoToModelTransformer from '../transformer/from-hardware-dto-to-model.transformer'

export const getPowerMetersByStorage = async (
  serialNo: string,
  authFetch: AuthFetchWithResponse,
  abortSignal?: AbortSignal
): Promise<ApiResult<PowerMeterListItem[], Error | AuthFetchError>> => {
  try {
    const response = await authFetch<PowerMeterIds>({
      path: `storages/${serialNo}/hardware/powermeters/overview`,
      method: 'GET',
      payload: undefined,
      abortSignal,
    })
    if (response.status === StatusCodes.OK) {
      const indexes = response.data.ids

      const transformedData = indexes.map(powerMeterIndex => ({
        storageSerialNo: serialNo,
        powerMeterIndex,
      }))
      return new ApiResult<PowerMeterListItem[], Error | AuthFetchError>(transformedData, false)
    }
    return new ApiResult<PowerMeterListItem[], Error | AuthFetchError>(null, true)
  } catch (error) {
    return handleFetchError<PowerMeterListItem[]>(error)
  }
}

/**
 *
 * return all Powermeters for a storage
 */
export const getPowerMeterListByStorage = async (
  serialNo: string | undefined,
  authFetch: AuthFetchWithResponse,
  abortSignal?: AbortSignal
): Promise<ApiResult<PowermeterHardwareModel[], Error | AuthFetchError>> => {
  try {
    const response = await authFetch<PowerMeterDto[]>({
      path: `storages/${serialNo}/hardware/powermeters`,
      method: 'GET',
      payload: undefined,
      abortSignal,
    })

    if (response.status === StatusCodes.OK) {
      const transformedData = response.data.map(powermeter => {
        return fromHardwareDtoToModelTransformer(powermeter)
      })

      return new ApiResult<PowermeterHardwareModel[], Error | AuthFetchError>(transformedData, false)
    }

    return new ApiResult<PowermeterHardwareModel[], Error | AuthFetchError>(null, true)
  } catch (error) {
    console.error(error)
    return handleFetchError<PowermeterHardwareModel[]>(error)
  }
}

/**
 * This function returns a list of power meter ids for a farm
 *
 * @param serialNo a id of the used storage
 * @param authFetch a method to fetch data from the backend
 * @param abortSignal used to terminate the fetch
 * @returns a list of power meter ids
 */
export const getFarmPowerMetersByStorage = async (
  serialNo: string,
  authFetch: AuthFetchWithResponse,
  abortSignal?: AbortSignal
): Promise<ApiResult<PowerMeterListItem[], Error | AuthFetchError>> => {
  try {
    const response = await authFetch<{ [key: number]: number[] }>({
      path: `storages/${serialNo}/hardware/powermeters/overview/master`,
      method: 'GET',
      payload: undefined,
      abortSignal,
    })
    if (response.status === StatusCodes.OK) {
      const transformedData = Object.entries(response.data).flatMap(([storageSerialNo, powerMeterIndexes]) => {
        return powerMeterIndexes.map(powerMeterIndex => ({
          storageSerialNo,
          powerMeterIndex,
        }))
      })
      return new ApiResult<PowerMeterListItem[], AuthFetchError | Error>(transformedData)
    }
    return new ApiResult<PowerMeterListItem[], Error | AuthFetchError>(null, true)
  } catch (error) {
    return handleFetchError<PowerMeterListItem[]>(error)
  }
}

export const getPowerMeter = async (
  serialNo: string,
  powerMeterKey: number,
  authFetch: AuthFetchWithResponse,
  abortSignal?: AbortSignal
): Promise<ApiResult<PowerMeterModel>> => {
  try {
    const response = await authFetch<PowerMeterOverviewDto>({
      path: `storages/${serialNo}/hardware/powermeters/overview/${powerMeterKey}`,
      method: 'GET',
      payload: undefined,
      abortSignal,
    })
    if (response.status === StatusCodes.OK) {
      const model = fromDtoToModel(response.data)
      return new ApiResult<PowerMeterModel>(model)
    }
    return new ApiResult<PowerMeterModel>(null, true)
  } catch (error) {
    return new ApiResult<PowerMeterModel>(null, true, error)
  }
}

/**
 * Provides current powermeter values based on differences between the history values for a given time-frame
 * @param powermeterId
 * @param storageId
 * @param from
 * @param to
 * @param authFetch
 * @returns a promise api result with the difference model of relevant powermeter values
 */
export const getPowerMeterDifferences = async (
  powermeterId: number,
  storageId: string,
  from: number,
  to: number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<DifferenceModel>> => {
  const response = await authFetch<DifferenceModel>({
    method: 'GET',
    path: `storages/${storageId}/powermeter-values/${powermeterId}/difference?from=${from}&to=${to}&approach=true`,
  })
  if (response.status === StatusCodes.OK) return new ApiResult<DifferenceModel>(response.data)

  return new ApiResult<DifferenceModel>(null, true)
}

export const updatePowerMetersSettings = async (
  serialNo: string,
  powerMeterKey: string,
  values: PowerMeterUpdateDto,
  authFetch: AuthFetchWithResponse
): Promise<void> => {
  const response = await authFetch<void>({
    path: `storages/${serialNo}/hardware/powermeters/overview/${powerMeterKey}`,
    method: 'PUT',
    payload: JSON.stringify(values),
  })
  if (response.status !== StatusCodes.NO_CONTENT) {
    throw new UnexpectedApiResponseError(response.status)
  }
}
